import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { customerAuthApi } from '../services/customerAuthApi';
import { apiFetch } from '../services/api';

// AuthContext — single source of truth for the customer's signed-in state.
// Wraps the app in App.js. Components read via useAuth().
//
// SEC-06 Phase 5: the customer session is an HttpOnly cookie now —
// JS can't see it. We can't decide "are they logged in?" by checking
// localStorage anymore; the only way is to ask the server. The boot
// pass hits /api/customer-auth/me; 200 means logged in, 401 means not.
// That's one extra round-trip per page load for an unauthed visitor,
// which is acceptable and is the standard cookie-auth pattern.
const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  // `loading` is true while we boot and ask the server who we are.
  // UI should wait on this before deciding whether to show "Sign in"
  // or "Account" links to avoid flicker.
  const [loading, setLoading] = useState(true);

  // Bootstrap — ask the server who's signed in. A 401 just means
  // "no session" (cookie absent / expired); not an error.
  useEffect(() => {
    let cancelled = false;
    customerAuthApi.me()
      .then((u) => { if (!cancelled) setUser(u); })
      .catch(() => { /* 401 or network failure → stay signed out */ })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const login = useCallback(async (email, password) => {
    const data = await customerAuthApi.login({ email, password });
    // Server set the customer_session + csrf_token cookies on the
    // response. Just record the user state — no token to stash.
    setUser(data.customer);
    return data.customer;
  }, []);

  const register = useCallback(async (payload) => {
    const data = await customerAuthApi.register(payload);
    setUser(data.customer);
    return data.customer;
  }, []);

  // SEC-06 Phase 5: logout is a server call that clears the HttpOnly
  // session cookie + the csrf_token cookie. JS can't clear them.
  // Fire-and-forget — even on network failure the UI returns to the
  // signed-out path immediately.
  const logout = useCallback(async () => {
    setUser(null);
    try {
      await apiFetch('/api/customer-auth/logout', { method: 'POST' });
    } catch {
      // Cookie may persist server-side; the user re-logs if needed.
    }
  }, []);

  // Re-fetch the profile (e.g. after an updateProfile from another tab).
  // Reuses the boot pattern: 200 → set user, 401 → clear.
  const refresh = useCallback(async () => {
    try {
      const u = await customerAuthApi.me();
      setUser(u);
      return u;
    } catch (err) {
      if (err.status === 401) setUser(null);
      return null;
    }
  }, []);

  const updateProfile = useCallback(async (patch) => {
    const u = await customerAuthApi.updateProfile(patch);
    setUser(u);
    return u;
  }, []);

  const value = { user, loading, login, register, logout, refresh, updateProfile };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within <AuthProvider>');
  return ctx;
}
